/*
Copyright 2022 Fondazione Bruno Kessler
Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
except in compliance with the Licence.

You may obtain a copy of the Licence at:
http://ec.europa.eu/idabc/eupl.html

Unless required by applicable law or agreed to in writing, software distributed under 
the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF 
ANY KIND, either express or implied. See the Licence for the specific language 
governing permissions and limitations under the Licence.
*/
import {ClientSideRowModelModule} from "@ag-grid-community/client-side-row-model";
import {ColDef, GridOptions, Module} from "@ag-grid-community/core";
import {formatDate} from "@angular/common";
import {ChangeDetectorRef, Component, Inject, LOCALE_ID, OnDestroy, OnInit, TemplateRef, ViewChild} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WithAgTableComponent} from "@app/models/components/with-ag-table.component";
import {CommonState} from "@app/store/common/common.state";
import {AddNotesDialogComponent} from "@doctor-panel-modules/patient-personal-data/components/add-notes-dialog/add-notes-dialog.component";
import {PatientPersonalDataActions} from "@doctor-panel-modules/patient-personal-data/store/patient-personal-data.actions";
import {PatientPersonalDataState} from "@doctor-panel-modules/patient-personal-data/store/patient-personal-data.state";
import {TP_ALL_SECTIONS} from "@doctor-panel-modules/patient-treatment-plan/models/tp-sections-info";
import {AlertController, ModalController} from "@ionic/angular";
import {CustomPreferencesService} from "@modules/base/services/custom-preferences.service";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {ClinicalDataNotes, TrecError} from "@trec/api";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {
  AG_GRID_LOCALE_IT,
  C3DateCellComponent,
  C3TextCellComponent,
  C3TmplCellComponent,
  C3TmplCellOptions,
} from "@trec/components/table";
import {Observable, Subscription} from "rxjs";

@Component({
  selector: "app-config-diario-anamnesi",
  templateUrl: "./config-diario-anamnesi.component.html",
  styleUrls: ["./config-diario-anamnesi.component.scss"],
  standalone: false,
})
export class ConfigDiarioAnamnesiComponent
  extends WithAgTableComponent<ClinicalDataNotes>
  implements OnInit, OnDestroy {
  protected customAgGridOptions: GridOptions;
  protected PREF_KEY_TABLE_SORT: string;
  protected PREF_KEY_HIDDEN_COLUMNS: string;
  /**
   * Info about treatment plan sections.
   */
  TP_SECTIONS_INFO = TP_ALL_SECTIONS;

  @Select(
    hasActionsExecuting([
      PatientPersonalDataActions.InsertNote,
      PatientPersonalDataActions.StoricoNotePaziente,
    ]),
  )
  loading$: Observable<boolean>;
  @Select(PatientPersonalDataState.storicoNote) historicalNotes$: Observable<ClinicalDataNotes[]>;

  @ViewChild("actionsCellTmpl")
  actionsCellTmpl!: TemplateRef<unknown>;

  private readonly subscr = new Subscription();

  agModules: Module[] = [ClientSideRowModelModule];
  historicalNotes: ClinicalDataNotes[] = [];

  patientId: string;

  agGridOptions: GridOptions = {
    localeText: AG_GRID_LOCALE_IT,
    domLayout: "autoHeight",
    pagination: true,
    paginationAutoPageSize: false,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50, 100],
    rowStyle: {background: "white"},
    getRowStyle: (params) => {
      if (params.data.reference) {
        return {"background-color": "#dcdfe6"};
      }
      return {"background-color": "white"};
    },
  };

  columnDefs: ColDef[];

  constructor(
    private store: Store,
    protected preferences: CustomPreferencesService,
    protected router: Router,
    protected route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private modalCtrl: ModalController, private alertCtrl: AlertController,
    @Inject(LOCALE_ID) private locale: string,
    private c3Snackbar: C3SnackbarStatusService,
  ) {
    super(preferences, router, route);
  }

  ngOnInit(): void {
    const patientId = this.store.selectSnapshot(CommonState.patientId);
    if (!patientId) {
      throw new Error(`patientId is null`);
    }
    this.patientId = patientId;

    this.subscr.add(
      this.historicalNotes$.subscribe((historicalNotes) => {
        this.historicalNotes = historicalNotes;
        this.cd.markForCheck();
      }),
    );

    this.store.dispatch(
      new PatientPersonalDataActions.StoricoNotePaziente({
        trecUserId: this.patientId,
      }),
    );
  }

  ngAfterViewInit() {
    this.columnDefs = [
      {
        headerName: `Data`,
        headerClass: "app-table-header-cell-text-wrap",
        field: "date",
        flex: 10,
        minWidth: 180,
        autoHeight: true,
        cellRendererSelector: () => ({component: C3DateCellComponent}),
        cellRenderer: (data) => {
          return formatDate(data.value, "dd/MM/yyyy HH:mm", this.locale);
        },
        tooltipValueGetter: (data) => {
          return formatDate(data.value, "dd/MM/yyyy HH:mm", this.locale);
        },
      },
      {
        headerName: `Operatore Sanitario`,
        field: "physician",
        flex: 15,
        minWidth: 100,
        autoHeight: true,
        tooltipField: "physician",
        cellStyle: {
          padding: "10px 10px",
        },
        cellRendererSelector: () => ({component: C3TextCellComponent}),
        comparator: (valueA: string, valueB: string) =>
          valueA?.toLowerCase().localeCompare(valueB?.toLowerCase()),
      },
      {
        headerName: `Ruolo dell'operatore sanitario`,
        field: "roleDescription",
        flex: 35,
        tooltipField: "roleDescription",
        minWidth: 100,
        autoHeight: true,
        cellStyle: {
          padding: "10px 10px",
        },
        cellRendererSelector: () => ({component: C3TextCellComponent}),
        comparator: (valueA: string, valueB: string) =>
          valueA?.toLowerCase().localeCompare(valueB?.toLowerCase()),
      },
      {
        headerName: `Note`,
        field: "text",
        flex: 40,
        minWidth: 190,
        cellRendererSelector: () => ({component: C3TextCellComponent}),
        tooltipField: "text",
        autoHeight: true,
        cellStyle: {
          padding: "10px 0",
        },
        comparator: (valueA: string, valueB: string) =>
          valueA?.toLowerCase().localeCompare(valueB?.toLowerCase()),
      },
      {
        headerName: `Azioni`,
        flex: 20,
        minWidth: 150,
        autoHeight: true,
        cellRendererSelector: () => ({component: C3TmplCellComponent}),
        cellRendererParams: {template: this.actionsCellTmpl} as C3TmplCellOptions,
      },
    ];
  }

  /**
   * Open dialog to add notes.
   */
  async onAddNote() {
    const patientId = this.store.selectSnapshot(CommonState.patientId);
    const modal = await this.modalCtrl.create({
      component: AddNotesDialogComponent,
      componentProps: {patientId: patientId},
      cssClass: "c3-modal-width-sm c3-modal-height-md",
    });
    return await modal.present();
  }

  async onEdit(data: ClinicalDataNotes) {
    const patientId = this.store.selectSnapshot(CommonState.patientId);
    const modal = await this.modalCtrl.create({
      component: AddNotesDialogComponent,
      componentProps: {
        patientId: patientId,
        editData: data
      },
      cssClass: "c3-modal-width-sm c3-modal-height-md",
    });
    return await modal.present();
  }

  async confirmDelete(data: ClinicalDataNotes) {
    const alert = await this.alertCtrl.create({
      header: 'Conferma',
      message: 'Sei sicuro di voler eliminare questa nota?',
      buttons: [
        {text: 'Annulla', role: 'cancel'},
        {text: 'Elimina', handler: () => {this.onDelete(data);}}
      ]
    });
    await alert.present();
  }

  async onDelete(data: ClinicalDataNotes) {
    const patientId = this.store.selectSnapshot(CommonState.patientId);
    let parentContext = localStorage.getItem("c3-curr-context");
    // patientId è compreso nella firma di ClinicalDataNotes ma non arriva da api
    // perciò lo aggiungo manualmente 

    this.store.dispatch(new PatientPersonalDataActions.DeleteNote({
      nota: {
        ...data,
        patientId: patientId || ""
      },
      context: parentContext
    }))
      .subscribe(
        () => {
          // Success
          this.c3Snackbar.showSuccess({message: "Nota eliminata con successo"});
          this.store.dispatch(new PatientPersonalDataActions.StoricoNotePaziente({
            trecUserId: this.patientId
          }))
          this.alertCtrl.dismiss();
        },
        (err: TrecError) => {
          this.c3Snackbar.showError({
            error: err,
            messagesMap: {
              "": "Si è verificato un errore durante l'eliminazione",
              "possible_new_bug": "Si è verificato un errore durante l'eliminazione",
              "anamnesi_note_expired": "La nota non può essere eliminata: sono trascorsi più di cinque giorni dall'inserimento"
            },
          });

          this.alertCtrl.dismiss();
        }
      );
  }

  ngOnDestroy() {
    this.subscr.unsubscribe();
  }
}
